/**
 * Centralized authentication error messages for consistent UX
 * across login, registration, and OAuth flows
 */

export const AUTH_ERRORS = {
  // Google OAuth errors
  GOOGLE_OAUTH_INIT_FAILED: 'Failed to initiate Google login. Please try again.',
  GOOGLE_OAUTH_CALLBACK_FAILED: 'Google authentication failed. Please try again.',
  GOOGLE_OAUTH_NOT_AVAILABLE: 'Google login is not available. Please contact your administrator.',
  GOOGLE_OAUTH_PERSONAL_EMAIL: 'Personal Gmail accounts are not allowed. Please use your work email address.',
  GOOGLE_OAUTH_WORKSPACE_ONLY: 'Only Google Workspace accounts are allowed. Personal Gmail accounts cannot be used.',
  GOOGLE_OAUTH_DOMAIN_MISMATCH: 'Email domain mismatch. Please use an email from your organization.',

  // GitHub OAuth errors
  GITHUB_OAUTH_INIT_FAILED: 'Failed to initiate GitHub login. Please try again.',
  GITHUB_OAUTH_CALLBACK_FAILED: 'GitHub authentication failed. Please try again.',
  GITHUB_OAUTH_NOT_AVAILABLE: 'GitHub login is not available. Please contact your administrator.',
  GITHUB_OAUTH_PERSONAL_EMAIL: 'Personal email accounts are not allowed. Please use your work email address.',
  GITHUB_OAUTH_NO_VERIFIED_EMAIL: 'No verified email found on your GitHub account. Please verify your email on GitHub.',

  // Okta SSO errors
  OKTA_SSO_INIT_FAILED: 'Failed to initiate SSO. Please try again.',
  OKTA_SSO_NOT_CONFIGURED: 'No SSO configuration found for your email domain. Please contact your administrator or use password login.',

  // General authentication errors
  LOGIN_FAILED: 'Login failed. Please check your credentials.',
  INVALID_CREDENTIALS: 'Invalid email or password.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address before logging in.',
  EMAIL_REQUIRED: 'Please enter your email address first.',
  PASSWORD_REQUIRED: 'Please enter your password.',

  // 2FA errors
  TFA_CODE_INVALID: 'Invalid verification code.',
  TFA_CODE_REQUIRED: 'Please enter the verification code.',
  TFA_FAILED: 'Two-factor authentication failed. Please try again.',

  // Session errors
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  UNAUTHORIZED: 'You are not authorized to access this resource.',

  // Network errors
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',

  // OAuth state errors
  OAUTH_STATE_INVALID: 'Invalid or expired authentication state. Please try logging in again.',
  OAUTH_STATE_EXPIRED: 'Authentication session expired. Please try again.',

  // Generic fallback
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

/**
 * Helper function to get a user-friendly error message
 * Falls back to a generic error if the specific error is not found
 */
export function getAuthErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    // Check if the error string matches any of our known errors
    for (const [, message] of Object.entries(AUTH_ERRORS)) {
      if (error.includes(message) || message.includes(error)) {
        return message;
      }
    }
    return error;
  }

  if (error instanceof Error) {
    return error.message || AUTH_ERRORS.UNKNOWN_ERROR;
  }

  return AUTH_ERRORS.UNKNOWN_ERROR;
}

/**
 * Check if an error is related to email verification
 */
export function isEmailVerificationError(error: unknown): boolean {
  const errorMessage = typeof error === 'string' ? error : (error as Error)?.message || '';
  return errorMessage.toLowerCase().includes('verify') ||
         errorMessage.toLowerCase().includes('verification');
}

/**
 * Check if an error is a personal email rejection
 */
export function isPersonalEmailError(error: unknown): boolean {
  const errorMessage = typeof error === 'string' ? error : (error as Error)?.message || '';
  return errorMessage.toLowerCase().includes('personal') ||
         errorMessage.toLowerCase().includes('gmail') ||
         errorMessage.toLowerCase().includes('work email');
}
